import { parseTimeRange } from "./calendar-utils";
import { currentTimeMinutes, getTodayKey } from "./logic";
import type { DailyItem, DisciplioSettings } from "./types";

export type DailyNotificationKind = "START" | "END_SOON";

export type DailyNotificationEvent = {
  id: string;
  itemId: string;
  itemTitle: string;
  kind: DailyNotificationKind;
  scheduledMinute: number;
  scheduledTime: string;
};

export type DailyNotificationCursor = {
  dateKey: string;
  operationalMinute: number;
};

function timeToMinutes(value: string): number {
  const [hour, minute] = value.split(":").map(Number);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return 0;
  return Math.max(0, Math.min(23, hour)) * 60 + Math.max(0, Math.min(59, minute));
}

function toOperationalMinute(clockMinute: number, dayStartTime: string): number {
  return (clockMinute - timeToMinutes(dayStartTime) + 1440) % 1440;
}

function toClockTime(minutes: number): string {
  const normalized = ((minutes % 1440) + 1440) % 1440;
  return `${String(Math.floor(normalized / 60)).padStart(2, "0")}:${String(normalized % 60).padStart(2, "0")}`;
}

export function currentDailyNotificationCursor(settings: DisciplioSettings, now = new Date()): DailyNotificationCursor {
  return {
    dateKey: getTodayKey(settings, now),
    operationalMinute: toOperationalMinute(currentTimeMinutes(settings, now), settings.dayStartTime),
  };
}

function occursBetween(eventMinute: number, previous: DailyNotificationCursor, current: DailyNotificationCursor): boolean {
  if (previous.dateKey !== current.dateKey) return eventMinute === current.operationalMinute;
  if (current.operationalMinute - previous.operationalMinute > 2) return eventMinute === current.operationalMinute;
  return eventMinute > previous.operationalMinute && eventMinute <= current.operationalMinute;
}

export function collectDueDailyNotifications(
  items: DailyItem[],
  settings: DisciplioSettings,
  previous: DailyNotificationCursor,
  now = new Date()
): { cursor: DailyNotificationCursor; events: DailyNotificationEvent[] } {
  const cursor = currentDailyNotificationCursor(settings, now);
  if (!settings.dailyGoalNotificationsEnabled || (previous.dateKey !== cursor.dateKey && previous.operationalMinute !== -1)) {
    return { cursor, events: [] };
  }

  const events = items.flatMap((item) => {
    if (item.done) return [];
    const parsed = parseTimeRange(item.time);
    if (!parsed) return [];

    const startOperationalMinute = toOperationalMinute(parsed.startMinutes, settings.dayStartTime);
    const start: DailyNotificationEvent = {
      id: `${cursor.dateKey}:${item.id}:START:${parsed.startMinutes}`,
      itemId: item.id,
      itemTitle: item.title,
      kind: "START",
      scheduledMinute: startOperationalMinute,
      scheduledTime: parsed.start,
    };
    const candidateEvents = [start];

    const lead = settings.dailyGoalEndReminderMinutes;
    if (parsed.endMinutes !== null && parsed.endMinutes > parsed.startMinutes && lead > 0 && lead < parsed.endMinutes - parsed.startMinutes) {
      const endSoonMinute = parsed.endMinutes - lead;
      candidateEvents.push({
        id: `${cursor.dateKey}:${item.id}:END_SOON:${endSoonMinute}`,
        itemId: item.id,
        itemTitle: item.title,
        kind: "END_SOON",
        scheduledMinute: toOperationalMinute(endSoonMinute, settings.dayStartTime),
        scheduledTime: toClockTime(endSoonMinute),
      });
    }
    return candidateEvents.filter((event) => occursBetween(event.scheduledMinute, previous, cursor));
  }).sort((first, second) => first.scheduledMinute - second.scheduledMinute || first.itemTitle.localeCompare(second.itemTitle));

  return { cursor, events };
}
